import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Triobot.css";
import API_URLS from "../apiUrls";
import { getAuth, onAuthStateChanged } from "firebase/auth";

/* ---------------- Types ---------------- */

type Sender = "bot" | "user";

type Step =
  | "loading"
  | "mainMenu"
  | "flightFrom"
  | "flightTo"
  | "flightDates"
  | "flightPax"
  | "hotelCity"
  | "hotelDates"
  | "hotelRooms"
  | "done";

interface Message {
  html: string;
  sender: Sender;
}

interface FlightLocationResult {
  airport_name: string;
  city_name: string;
  country_name: string;
  iata_code: string;
}

interface HotelLocationResult {
  CityCode: string;
  CityName: string;
  CountryName: string;
  CountryCode: string;
}

interface PaxRooms {
  Adults: number;
  Children: number;
  ChildrenAges: number[];
}

/* Escape user/API-sourced strings before injecting into innerHTML */
const escHtml = (s: string) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const todayStr = () => new Date().toISOString().slice(0, 10);

/* ---------------- Component ---------------- */

const TrioBot: React.FC = () => {
  const auth = getAuth();
  const navigate = useNavigate();

  /* ---------------- UI state ---------------- */
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  const [step, setStep] = useState<Step>("loading");

  const greetedRef = useRef(false);
  const closeTimerRef = useRef<number | null>(null);
  const clickHandlerRef = useRef<((e: any) => void) | null>(null);

  const windowRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  /* mirror state into refs so click-handler closures stay fresh */
  const messagesRef = useRef<Message[]>([]);
  const stepRef = useRef<Step>("loading");

  /* last completed search details — used when "Open in Flight/Hotel List" is clicked */
  const lastFlightNavStateRef = useRef<{ flightSearchDetails: any; flightType: string } | null>(null);
  const lastHotelNavStateRef = useRef<{ hotelSearchDetails: any } | null>(null);

  /* ---------------- flight inputs ---------------- */
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [departDate, setDepartDate] = useState("");
  const [adultCount, setAdultCount] = useState(1);
  const [childCount, setChildCount] = useState(0);
  const [infantCount, setInfantCount] = useState(0);

  /* ---------------- hotel inputs ---------------- */
  const [hotelCity, setHotelCity] = useState<{
    cityName: string;
    countryName: string;
    cityCode: string;
    countryCode: string;
  } | null>(null);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [paxRooms, setPaxRooms] = useState<PaxRooms[]>([
    { Adults: 2, Children: 0, ChildrenAges: [] },
  ]);

  /* ---------------- suggestions ---------------- */
  const [flightOptions, setFlightOptions] = useState<FlightLocationResult[]>([]);
  const [hotelOptions, setHotelOptions] = useState<HotelLocationResult[]>([]);
  const [hotelSearchTerm, setHotelSearchTerm] = useState("");
  const [fromSearchTerm, setFromSearchTerm] = useState("");
  const [toSearchTerm, setToSearchTerm] = useState("");

  /* ---------------- Helpers ---------------- */
  const addMsg = (html: string, sender: Sender) =>
    setMessages((prev) => [...prev, { html, sender }]);

  const replaceTypingWith = (html?: string) => {
    setIsTyping(false);
    if (html) addMsg(html, "bot");
  };

  const resetChat = () => {
    setMessages([]);
    setStep("loading");
    greetedRef.current = false;
    try { sessionStorage.removeItem("triobot_session_chat"); } catch {}
    /* clear all search inputs so a fresh chat starts clean */
    setFromCity(""); setToCity(""); setDepartDate("");
    setAdultCount(1); setChildCount(0); setInfantCount(0);
    setFromSearchTerm(""); setToSearchTerm(""); setFlightOptions([]);
    setHotelCity(null); setCheckIn(""); setCheckOut("");
    setPaxRooms([{ Adults: 2, Children: 0, ChildrenAges: [] }]);
    setHotelSearchTerm(""); setHotelOptions([]);
    lastFlightNavStateRef.current = null;
    lastHotelNavStateRef.current = null;
  };

  const resetAndClose = () => {
    resetChat();
    setIsOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const restartFresh = () => {
    resetAndClose();
    setTimeout(() => setIsOpen(true), 150);
  };

  /* Save current chat to sessionStorage so Back navigation can restore it */
  const persistChat = () => {
    try {
      sessionStorage.setItem(
        "triobot_session_chat",
        JSON.stringify({ messages: messagesRef.current, step: stepRef.current })
      );
    } catch {}
  };

  const startAutoCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => setIsOpen(false), 60_000);
  };

  /* ---------------- Firebase Auth ---------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setIsAuthenticated(true);
        setIdToken(token);
      } else {
        setIsAuthenticated(false);
        setIdToken(null);
      }
    });
    return () => unsub();
  }, []);

  /* Keep refs in sync with state so closures captured in effects stay fresh */
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { stepRef.current = step; }, [step]);

  /* Restore chat that was persisted before navigating to flight/hotel list */
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("triobot_session_chat");
      if (!raw) return;
      const { messages: saved, step: savedStep } = JSON.parse(raw);
      sessionStorage.removeItem("triobot_session_chat");
      if (Array.isArray(saved) && saved.length > 0) {
        setMessages(saved);
        setStep(savedStep || "done");
        greetedRef.current = true;
      }
    } catch {}
  }, []);

  /* ---------------- Auto-scroll ---------------- */
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping]);

  /* ---------------- click outside closes bot ---------------- */
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const icon = document.querySelector(".chatbot-icon");
      if (
        windowRef.current &&
        !windowRef.current.contains(e.target as Node) &&
        icon &&
        !icon.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen]);

  /* ---------------- Greeting ---------------- */
  useEffect(() => {
    if (!isOpen || greetedRef.current) return;

    (async () => {
      greetedRef.current = true;
      try {
        const r = await fetch(API_URLS.BOT.GET_MESSAGE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: "hi hello greetings" }),
        });
        const data = await r.json();
        addMsg(data.message || "Hi! Welcome 👋", "bot");
      } catch {
        addMsg("Hi! Welcome to VoyAIage 👋", "bot");
      }

      addMsg(
        `What are you looking for?<br/>
        <div class="menu-buttons">
          <button class="btn-red" data-action="flight">Flight Booking ✈️</button>
          <button class="btn-red-outline" data-action="hotel">Hotel Booking 🏨</button>
          <button class="btn-grey" data-action="callback">Request Callback 📞</button>
        </div>`,
        "bot"
      );
      setStep("mainMenu");
    })();
  }, [isOpen]);

  /* ---------------- delegate inside bubbles (click handler) ---------------- */
  useEffect(() => {
    if (!isOpen) return;
    const body = bodyRef.current;
    if (!body) return;

    const onClick = async (e: any) => {
      const btn: HTMLButtonElement | null = e.target?.closest("button");
      if (!btn) return;
      const action = btn.getAttribute("data-action");
      if (!action) return;

      if (action === "flight") {
        addMsg("Flight Booking", "user");
        addMsg("Great! Where are you flying from? (type to search)", "bot");
        setStep("flightFrom");
        return;
      }

      if (action === "hotel") {
        addMsg("Hotel Booking", "user");
        addMsg("Sure! Which city? (type to search)", "bot");
        setStep("hotelCity");
        return;
      }

      if (action === "callback") {
        addMsg("Request Callback", "user");

        if (!isAuthenticated || !idToken) {
          addMsg(
            "🔐 Please login to request a callback. Once logged in, I can call you instantly.",
            "bot"
          );
          return;
        }

        try {
          const r = await fetch(API_URLS.BOT.REQUEST_CALLBACK, {
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
          });
          const data = await r.json();
          const callbackPhone = data.phone || "";
          const masked = callbackPhone.slice(0, 3) + "*****" + callbackPhone.slice(-2);
          addMsg(
            `📞 We'll reach out to you on <strong>${escHtml(masked)}</strong>.<br/>
             Or call us at <strong>+91 2269976574</strong>.<br/><br/>
             This window will stay open for a minute.`,
            "bot"
          );
          startAutoCloseTimer();
        } catch {
          addMsg("Failed to request callback. Please try again.", "bot");
        }
        return;
      }

      if (action === "new-chat") {
        restartFresh();
        return;
      }

      if (action === "open-flight-list-card" || action === "open-flight-list-all") {
        if (lastFlightNavStateRef.current) {
          persistChat();
          navigate("/flight/flight-list", { state: lastFlightNavStateRef.current });
        }
        return;
      }

      if (action === "open-hotel-grid") {
        if (lastHotelNavStateRef.current) {
          persistChat();
          navigate("/hotel/hotel-grid", { state: lastHotelNavStateRef.current });
        }
        return;
      }
    };

    clickHandlerRef.current = onClick;
    body.addEventListener("click", onClick);
    return () => {
      if (clickHandlerRef.current) {
        body.removeEventListener("click", clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    };
  }, [isOpen, isAuthenticated, idToken]);

  /* ---------------- search: flights (airport/city) ---------------- */
  const searchAirports = async (q: string) => {
    if (q.trim().length < 2) return setFlightOptions([]);
    try {
      const res = await fetch(API_URLS.FLIGHTS.SEARCH_LOCATION + encodeURIComponent(q));
      const data = await res.json();
      setFlightOptions(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setFlightOptions([]);
    }
  };

  /* ---------------- search: hotels (cities) ---------------- */
  const searchHotelCities = async (q: string) => {
    if (q.trim().length < 2) return setHotelOptions([]);
    try {
      const res = await fetch(`${API_URLS.HOTELS.SEARCH_CITIES}?query=${encodeURIComponent(q)}`);
      const data = await res.json();
      setHotelOptions(Array.isArray(data?.results) ? data.results : []);
    } catch {
      setHotelOptions([]);
    }
  };

  /* ---------------- Flights → navigate to flight-list with router state ---------------- */
  const doFlightSearch = async () => {
    lastFlightNavStateRef.current = {
      flightSearchDetails: {
        from: { iataCode: fromCity },
        to: { iataCode: toCity },
        departureDate: departDate,
        returnDate: null,
        cabinClass: "Economy",
        passengers: { adults: adultCount, children: childCount, infants: infantCount },
      },
      flightType: "oneway",
    };

    const payload = {
      EndUserIp: "106.219.87.88",
      AdultCount: adultCount,
      ChildCount: childCount,
      InfantCount: infantCount,
      DirectFlight: false,
      OneStopFlight: false,
      JourneyType: 1,
      PreferredAirlines: [],
      Segments: [
        {
          Origin: fromCity,
          Destination: toCity,
          FlightCabinClass: 1,
          PreferredDepartureTime: `${departDate}T00:00:00`,
          PreferredArrivalTime: `${departDate}T00:00:00`,
        },
      ],
    };

    try {
      setIsTyping(true);
      const r = await fetch(API_URLS.FLIGHTS.REALTIME_FLIGHT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json();

      const all = data?.Response?.Results?.[0] || [];
      if (!Array.isArray(all) || all.length === 0) {
        replaceTypingWith("No flights found. Try different dates.");
        setStep("done");
        addMsg(`<div class="center"><button class="btn-grey" data-action="new-chat">Start new chat</button></div>`, "bot");
        return;
      }

      const sorted = [...all].sort((a: any, b: any) => a?.Fare?.OfferedFare - b?.Fare?.OfferedFare);
      const top = sorted.slice(0, 10);

      let html = `<div class="question">Top ${top.length} cheapest flights</div>`;
      top.forEach((f: any) => {
        const seg = f?.Segments?.[0]?.[0];
        if (!seg) return;
        const airline = escHtml(`${seg?.Airline?.AirlineName || ""} ${seg?.Airline?.FlightNumber || ""}`.trim());
        const dep = new Date(seg?.Origin?.DepTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const arr = new Date(seg?.Destination?.ArrTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const price = f?.Fare?.OfferedFare?.toLocaleString?.("en-IN") ?? f?.Fare?.OfferedFare;
        const originCity = escHtml(seg?.Origin?.Airport?.CityName || "");
        const originCode = escHtml(seg?.Origin?.Airport?.AirportCode || "");
        const destCity = escHtml(seg?.Destination?.Airport?.CityName || "");
        const destCode = escHtml(seg?.Destination?.Airport?.AirportCode || "");
        html += `
          <div class="triocard triocard--flight">
            <div class="triocard-left">
              <div class="triocard-title">${airline}</div>
              <div class="triocard-sub">
                ${originCity} (${originCode})
                <span class="triocard-arrow">→</span>
                ${destCity} (${destCode})
              </div>
              <div class="triocard-meta">${dep} – ${arr}</div>
            </div>
            <div class="triocard-right">
              <div class="triocard-price">₹${price}</div>
              <button class="btn-red" data-action="open-flight-list-card">Open in Flight List</button>
            </div>
          </div>`;
      });
      replaceTypingWith(html);

      const moreAvail = all.length > 10;
      const footer = `
        <div class="center" style="margin-top:8px">
          ${moreAvail ? `<button data-action="open-flight-list-all" class="btn-red-outline">See all results →</button>` : ""}
          <button data-action="new-chat" class="btn-grey" style="margin-left:8px">Start new chat</button>
        </div>`;
      addMsg(footer, "bot");
      setStep("done");
    } catch {
      replaceTypingWith("Something went wrong searching flights.");
      setStep("done");
      addMsg(`<div class="center"><button class="btn-grey" data-action="new-chat">Start new chat</button></div>`, "bot");
    } finally {
      setIsTyping(false);
    }
  };

  /* ---------------- Hotels → navigate to hotel-grid with router state ---------------- */
  const doHotelSearch = async () => {
    if (!hotelCity?.cityCode) {
      addMsg("Pick a valid hotel city (use suggestions).", "bot");
      return;
    }
    if (paxRooms.length === 0) {
      addMsg("Please add at least one room.", "bot");
      return;
    }

    lastHotelNavStateRef.current = {
      hotelSearchDetails: {
        city: { cityCode: hotelCity.cityCode, name: hotelCity.cityName, country: hotelCity.countryName },
        checkInDate: checkIn,
        checkOutDate: checkOut,
        PaxRooms: paxRooms,
      },
    };

    const payload = {
      CityCode: hotelCity.cityCode,
      Language: "EN",
      CheckIn: checkIn,
      CheckOut: checkOut,
      GuestNationality: "IN",
      PaxRooms: paxRooms,
      ResponseTime: 20,
      IsDetailedResponse: true,
      Filters: { Refundable: false, NoOfRooms: paxRooms.length },
    };

    try {
      setIsTyping(true);
      const r = await fetch(API_URLS.HOTELS.HOTEL_SEARCH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      const hotels: any[] = data?.hotels || [];
      if (!hotels.length) {
        replaceTypingWith("No hotels found for those dates.");
        setStep("done");
        addMsg(`<div class="center"><button class="btn-grey" data-action="new-chat">Start new chat</button></div>`, "bot");
        return;
      }

      const top = [...hotels].sort((a, b) => (a.Price || 0) - (b.Price || 0)).slice(0, 10);

      let html = `<div class="question">Top ${top.length} cheapest stays</div>`;
      top.forEach((h) => {
        const price = (h.Price || 0).toLocaleString("en-IN");
        const name = escHtml(h.Name || "");
        const city = escHtml(h.City || "");
        const country = escHtml(h.Country || "");
        const cityLabel = `${city}${country ? ", " + country : ""}`;
        html += `
          <div class="triocard triocard--hotel">
            <div class="triocard-left">
              <div class="triocard-title">${name}</div>
              <div class="triocard-sub">${cityLabel}</div>
            </div>
            <div class="triocard-right">
              <div class="triocard-price">₹${price}</div>
              <button class="btn-red" data-action="open-hotel-grid">Open in Hotel Grid</button>
            </div>
          </div>`;
      });
      replaceTypingWith(html);

      const moreAvail = hotels.length > 10;
      const footer = `
        <div class="center" style="margin-top:8px">
          ${moreAvail ? `<button data-action="open-hotel-grid" class="btn-red-outline">See all results →</button>` : ""}
          <button data-action="new-chat" class="btn-grey" style="margin-left:8px">Start new chat</button>
        </div>`;
      addMsg(footer, "bot");
      setStep("done");
    } catch {
      replaceTypingWith("Something went wrong searching hotels.");
      setStep("done");
      addMsg(`<div class="center"><button class="btn-grey" data-action="new-chat">Start new chat</button></div>`, "bot");
    } finally {
      setIsTyping(false);
    }
  };

  /* ---------------- Render ---------------- */
  const today = todayStr();

  return (
    <>
      <div className="chatbot-icon" onClick={() => setIsOpen(!isOpen)}>
        <img src="/assets/img/Revised.png" alt="Chatbot" />
        {!isOpen && <div className="chatbot-tooltip">Hi, can I help you?</div>}
      </div>

      {isOpen && (
        <div className="chatbot-window chatbot-window--xl is-open" ref={windowRef}>
          <div className="chatbot-header">
            <span className="brand">Voy<span className="brand-em">AI</span>age</span>
          </div>

          <div className="chatbot-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-row ${m.sender}`}>
                {m.sender === "bot" && <div className="avatar bot-avatar">🤖</div>}
                <div className="chat-bubble chat-bubble--light" dangerouslySetInnerHTML={{ __html: m.html }} />
                {m.sender === "user" && <div className="avatar user-avatar">🧑</div>}
              </div>
            ))}

            {isTyping && (
              <div className="chat-row bot">
                <div className="avatar bot-avatar">🤖</div>
                <div className="chat-bubble chat-bubble--light">
                  <span className="typing"><span></span><span></span><span></span></span>
                </div>
              </div>
            )}

            {(step === "flightFrom" || step === "flightTo") && (
              <div className="chat-row user-input">
                <input
                  value={step === "flightFrom" ? fromSearchTerm : toSearchTerm}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (step === "flightFrom") setFromSearchTerm(val);
                    else setToSearchTerm(val);
                    searchAirports(val);
                  }}
                  placeholder={step === "flightFrom" ? "From (type airport/city)" : "To (type airport/city)"}
                />
                {flightOptions.length > 0 && (
                  <div className="suggestions">
                    {flightOptions.map((c, idx) => (
                      <div
                        key={`${c.iata_code}-${idx}`}
                        onClick={() => {
                          if (step === "flightFrom") {
                            setFromCity(c.iata_code);
                            setFromSearchTerm(`${c.city_name} (${c.iata_code})`);
                            addMsg(`From: ${c.city_name} (${c.iata_code})`, "user");
                            setStep("flightTo");
                          } else {
                            setToCity(c.iata_code);
                            setToSearchTerm(`${c.city_name} (${c.iata_code})`);
                            addMsg(`To: ${c.city_name} (${c.iata_code})`, "user");
                            addMsg("Select departure date", "bot");
                            setStep("flightDates");
                          }
                          setFlightOptions([]);
                        }}
                      >
                        ➡ {c.city_name} ({c.iata_code}) — {c.airport_name}, {c.country_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === "flightDates" && (
              <div className="chat-row user-input">
                <span className="question">Select departure date</span>
                <input
                  type="date"
                  min={today}
                  value={departDate}
                  onChange={(e) => setDepartDate(e.target.value)}
                />
                <button
                  className="btn-red"
                  onClick={() => {
                    if (!departDate) {
                      addMsg("Please select a departure date.", "bot");
                      return;
                    }
                    const pretty = new Date(departDate).toLocaleDateString("en-IN", {
                      weekday: "short", day: "2-digit", month: "short", year: "numeric",
                    });
                    addMsg(`Departure: ${pretty}`, "user");
                    setStep("flightPax");
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {step === "flightPax" && (
              <div className="chat-row user-input">
                <span className="question">Passengers</span>
                <div className="pax-row">
                  <label>Adults</label>
                  <input type="number" min={1} value={adultCount} onChange={(e) => setAdultCount(Math.max(1, Number(e.target.value)))} />
                </div>
                <div className="pax-row">
                  <label>Children</label>
                  <input type="number" min={0} value={childCount} onChange={(e) => setChildCount(Math.max(0, Number(e.target.value)))} />
                </div>
                <div className="pax-row">
                  <label>Infants</label>
                  <input type="number" min={0} value={infantCount} onChange={(e) => setInfantCount(Math.max(0, Number(e.target.value)))} />
                </div>
                <button className="btn-red" onClick={doFlightSearch}>Search Flights</button>
              </div>
            )}

            {step === "hotelCity" && (
              <div className="chat-row user-input">
                <input
                  value={hotelSearchTerm}
                  onChange={(e) => {
                    const val = e.target.value;
                    setHotelSearchTerm(val);
                    searchHotelCities(val);
                  }}
                  placeholder="Type city (e.g., Mumbai, Bangkok)"
                />
                {hotelOptions.length > 0 && (
                  <div className="suggestions">
                    {hotelOptions.map((loc, index) => (
                      <div
                        key={`${loc.CityCode}-${index}`}
                        onClick={() => {
                          setHotelCity({
                            cityName: loc.CityName,
                            countryName: loc.CountryName,
                            cityCode: loc.CityCode,
                            countryCode: loc.CountryCode,
                          });
                          setHotelSearchTerm(`${loc.CityName}, ${loc.CountryName}`);
                          addMsg(`City: ${loc.CityName}, ${loc.CountryName}`, "user");
                          setHotelOptions([]);
                          setStep("hotelDates");
                        }}
                      >
                        ➡ {loc.CityName}, {loc.CountryName}
                      </div>
                    ))}
                  </div>
                )}
                {hotelCity && (
                  <button className="btn-red" style={{ marginTop: 6 }} onClick={() => setStep("hotelDates")}>
                    Next
                  </button>
                )}
              </div>
            )}

            {step === "hotelDates" && (
              <div className="chat-row user-input">
                <span className="question">Select dates</span>
                <input
                  type="date"
                  min={today}
                  value={checkIn}
                  onChange={(e) => { setCheckIn(e.target.value); setCheckOut(""); }}
                />
                <input
                  type="date"
                  min={checkIn || today}
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                />
                <button
                  className="btn-red"
                  onClick={() => {
                    if (!checkIn || !checkOut) {
                      addMsg("Please select both check-in and check-out dates.", "bot");
                      return;
                    }
                    if (checkOut <= checkIn) {
                      addMsg("Check-out must be after check-in.", "bot");
                      return;
                    }
                    const ci = new Date(checkIn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                    const co = new Date(checkOut).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                    addMsg(`Stay: ${ci} → ${co}`, "user");
                    setStep("hotelRooms");
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {step === "hotelRooms" && (
              <div className="chat-row user-input">
                <span className="question">Rooms (defaults to 1×(2 Adults))</span>
                <button className="btn-red-outline" onClick={() => setPaxRooms((prev) => [...prev, { Adults: 2, Children: 0, ChildrenAges: [] }])}>
                  + Add Room
                </button>
                {paxRooms.map((r, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 8, width: "100%", marginTop: 6 }}>
                    <input
                      type="number" min={1} value={r.Adults}
                      onChange={(e) => {
                        const v = Math.max(1, Number(e.target.value));
                        setPaxRooms((prev) => prev.map((x, i) => (i === idx ? { ...x, Adults: v } : x)));
                      }}
                      placeholder="Adults"
                    />
                    <input
                      type="number" min={0} value={r.Children}
                      onChange={(e) => {
                        const v = Math.max(0, Number(e.target.value));
                        /* one age entry (5 yrs) per child */
                        setPaxRooms((prev) => prev.map((x, i) =>
                          i === idx ? { ...x, Children: v, ChildrenAges: Array(v).fill(5) } : x
                        ));
                      }}
                      placeholder="Children"
                    />
                    <button
                      className="btn-grey"
                      disabled={paxRooms.length === 1}
                      onClick={() => setPaxRooms((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button className="btn-red" onClick={doHotelSearch}>Search Hotels</button>
              </div>
            )}

          </div>
        </div>
      )}
    </>
  );
};

export default TrioBot;
